/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { merge, Observable, tap, filter, Subscription, take, skip } from 'rxjs';
import { BlobType } from '@zumer/snapdom';
import { topMentionsMADExportStyleOverrides } from '@Monitor/Shared/components/TopMentionsMAD/top-mentions-mad-chart.component';

import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '../../../../../Common/Charts/components/CardChart/card-chart.component';
import { SocialSeriesMetricsEnum } from '../../../../../Social/Intelligence/enums/social-series-metrics.enum';
import { MADSummaryService } from './mad-summary.service';
import { ReportSeriesModel } from '../../../../../Common/Reports/models/report-series.model';
import { ReportDateRangeModel } from '../../../../../Common/Reports/models/report-date-range.model';
import { MADSummarySeriesTypesEnum } from '../../../enums/mad-series-types.enum';
import { DateValueXYChart } from '../../../../../Common/Charts/models/date-value-x-y-chart.model';
import { ReportsChartSeriesConverterService } from '../../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportBlockTypeEnum } from '../../../../../Common/Reports/enums/report-block-type.enum';
import { ChartSpecificTypesEnum } from '../../../../../Common/Reports/enums/chart-specific-types.enum';
import { ChartMetadataModel } from '../../../../../Common/Charts/models/chart-metadata.model';
import { DateAndValueXYChartUnitEnum } from '../../../../../Common/Charts/enums/date-and-value-x-y-chart-unit.enum';
import { DateAndValueXYChartOptionsModel } from '../../../../../Common/Charts/models/date-and-value-x-y-chart-options.model';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { ChartStylesModel } from '../../../../../Common/Charts/models/chart-styles.model';
import { ReportsChartDetailService } from '../../../../../Common/Reports/services/reports-chart-detail.service';
import { ChartModel } from '../../../../../Common/Charts/models/chart.model';
import { MapChartColorSchemeEnum, MapChartModel } from '../../../../../Common/Charts/models/map-chart.model';
import { AbstractChartOptionsModel } from '../../../../../Common/Charts/models/abstract-chart-options.model';
import { DropdownTypesEnum } from '../../../../../Common/Forms/enums/dropdown-types.enum';
import { AgprComponentStyleEnum } from '../../../../../Common/Basic/enums/social-analysis-types.enum';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { TopicsService } from '../../../../../Monitor/Core/services/topics.service';
import { SentimentModel } from '../../../../../Monitor/Shared/models/sentiment.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { MADChartOptionsEnum } from '../../../enums/mad-chart-options.enum';
import { ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { CategoryValueXYChart } from '@Common/Charts/models/category-value-x-y-chart.model';
import { CategoryValueXYChartOptions } from '@Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';
import { MonitoringDashboardSearchService } from '@Monitor/Core/services/monitoring-dashboard-search.service';
import { MediaTypeEnum, mediaTypeColorKeyMap } from '@Common/Basic/enums/media-type.enum';
import { ChartDisplaySettingsModel } from '@Common/Charts/models/chart-display-settings.model';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MADSeriesMap } from '@Monitor/Shared/services/mad-charts.service';
import { PreviousPeriodBarColorsEnum } from '@Common/Reports/enums/previous-period-bar-colors.enum';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { ReportSeriesFacetGeneratorEnum } from '@Common/Reports/models/report-series-facet-generator.enum';
import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';
import * as am4core from '@amcharts/amcharts4/core';
import { SocialChartExportModel, SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { reportBlockTypesMap } from '@Common/Reports/models/reports-block-types.model';
import { LabelDisplayOptionsEnum } from '@Common/Misc/enums/label-display-options.enum';
import { Am5DateValueXYChart } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5ChartTooltipService } from '@Common/Charts/services/am5-chart-tooltip.service';
import { Am5ChartConfigurationService } from '@Common/Reports/services/am5-chart-configuration.service';
import { UserCurrency } from '@Core/Users/enums/user-currency';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { Am5ChartSettingsService } from '@Common/Reports/services/am5-chart-settings.service';
import { Am5Chart } from '@Common/Charts/models/am5-chart.model';
import { ChartTimeIntervalsEnum } from '@Common/Reports/enums/chart-time-intervals.enum';
import { TimeHelper } from '@Common/Forms/helpers/time.helper';


@Component({
    selector: 'agpr-mad-summary',
    templateUrl: './mad-summary.component.html',
    styleUrls: ['./mad-summary.component.scss'],
    providers: [Am5ChartTooltipService],
    standalone: false
})
export class MADSummaryComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() loading: boolean = false;

    @Output() reportSeriesReady: EventEmitter<MADSeriesMap> = new EventEmitter<MADSeriesMap>();
    @Output() reportSeriesChange: EventEmitter<{oldSeriesTypes: any[]; newSeries: ReportSeriesModel[]}> = new EventEmitter();

    seriesMap: MADSeriesMap;
    classToChartModelMap: Map<string, ChartModel | Am5Chart>;
    classToReportSeriesMap: Map<string, ReportSeriesModel[]>;
    baseSentiments: SentimentModel[] = [];
    currencyCode: UserCurrency;
    timeHelper: TimeHelper = new TimeHelper();

    totalVolumeCardSeries: ReportSeriesModel[] = [];
    totalVolumeCardSeriesTypes = [MADSummarySeriesTypesEnum.totalVolumeCard, MADSummarySeriesTypesEnum.totalVolumeCardPreviousPeriod];
    totalImpressionCardSeries: ReportSeriesModel[] = [];
    totalImpressionCardSeriesTypes = [MADSummarySeriesTypesEnum.totalImpressionCard, MADSummarySeriesTypesEnum.totalImpressionCardPreviousPeriod];
    totalAVECardSeries: ReportSeriesModel[] = [];
    totalAVECardSeriesTypes = [MADSummarySeriesTypesEnum.totalAVECard, MADSummarySeriesTypesEnum.totalAVECardPreviousPeriod];

    topOutletsSeries: ReportSeriesModel;
    selectedTopOutletsMetric: ChartMetricsEnum = ChartMetricsEnum.VOLUME;
    topOutletsBlock: BlockModel;
    topAuthorsSeries: ReportSeriesModel;
    selectedTopAuthorsMetric: ChartMetricsEnum = ChartMetricsEnum.VOLUME;
    topAuthorsBlock: BlockModel;

    summaryTrendLineChart: Am5DateValueXYChart;
    summaryTrendLineChartClass: string = 'agpr-madSummary-summaryTrendLineChart';
    summaryTrendLineChartNoData: boolean = false;
    summaryTrendLineSeries: ReportSeriesModel[];

    positiveCardSeries: ReportSeriesModel[] = [];
    positiveCardSeriesTypes = [MADSummarySeriesTypesEnum.positive, MADSummarySeriesTypesEnum.positivePreviousPeriod];
    negativeCardSeries: ReportSeriesModel[] = [];
    negativeCardSeriesTypes = [MADSummarySeriesTypesEnum.negative, MADSummarySeriesTypesEnum.negativePreviousPeriod];
    averageDomainAuthorityCardSeries: ReportSeriesModel[] = [];
    averageDomainAuthorityCardSeriesTypes = [MADSummarySeriesTypesEnum.averageDomainAuthority, MADSummarySeriesTypesEnum.averageDomainAuthorityPreviousPeriod];

    mapChart: MapChartModel;
    mapChartClass: string = 'agpr-madSummary-mapChart';
    selectedMapChartDropdownOption: DropdownOptionModel;
    mapChartNoData: boolean = false;

    mediaTypeBreakdownChart: CategoryValueXYChart;
    mediaTypeBreakdownSeries: ReportSeriesModel;
    mediaTypeBreakdownChartClass: string = 'agpr-madSummary-mediaTypeBreakdownChart';
    mediaTypeBreakdownChartNoData: boolean = false;
    selectedMediaTypeBreakdownChartDropdownOption: DropdownOptionModel<MADChartOptionsEnum>;

    topMentionsSeries: ReportSeriesModel;
    selectedTopMentionsSortMetric: MADChartOptionsEnum = MADChartOptionsEnum.IMPRESSIONS;

    subscriptions: Subscription[] = [];

    readonly id: string = 'mad_summary';
    readonly cardChartComponentHighlightColors = CardChartComponentHighlightColorEnum;
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly cardChartLayoutTypes = CardChartComponentLayoutEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyles = AgprComponentStyleEnum;
    readonly mapChartDropdownOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.VOLUME}`, MADChartOptionsEnum.VOLUME),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.IMPRESSIONS}`, MADChartOptionsEnum.IMPRESSIONS),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.AVE}`, MADChartOptionsEnum.AVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.POSITIVE}`, MADChartOptionsEnum.POSITIVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.NEGATIVE}`, MADChartOptionsEnum.NEGATIVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.DOMAIN_AUTHORITY}`, MADChartOptionsEnum.DOMAIN_AUTHORITY)

    ];
    readonly entityType = EntityType;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly mediaTypeBreakdownDropdownOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(
            `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.VOLUME}`,
            MADChartOptionsEnum.VOLUME
        ),
        new DropdownOptionModel(
            `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.IMPRESSIONS}`,
            MADChartOptionsEnum.IMPRESSIONS
        ),
        new DropdownOptionModel(
            `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.AVE}`,
            MADChartOptionsEnum.AVE
        ),
        new DropdownOptionModel(
            `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.POSITIVE}`,
            MADChartOptionsEnum.POSITIVE
        ),
        new DropdownOptionModel(
            `Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.NEGATIVE}`,
            MADChartOptionsEnum.NEGATIVE
        )
    ];
    readonly messageSeverities = messageSeverityEnum;
    readonly MADSummarySeriesTypes = MADSummarySeriesTypesEnum;
    readonly madSummaryChartTypeEnum = ReportBlockTypeEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];

    constructor(
        public madSummaryService: MADSummaryService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsChartDetailService: ReportsChartDetailService,
        public topicsService: TopicsService,
        public dashboardSearchService: MonitoringDashboardSearchService,
        public localizationService: LocalizationService,
        public chartExportService: SocialChartExportService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService,
        public currentUserStateService: CurrentUserStateService,
        @Inject (APP_BASE_HREF) public appBaseHref: string
    ) {}

    // the view mention link is in amcharts, and we couldn't use our normal way to bind them
    @HostListener('click', ['$event.target']) onClick(event) {
        if (event?.classList?.contains('viewMentionLink')) {
            let chartDiv = event;
            while (chartDiv && !chartDiv.classList.contains('agpr-mad-chartAnchor')) {
                chartDiv = chartDiv.parentElement;
            }
            if (!chartDiv) {
                return;
            }
            const chartModel = this.classToChartModelMap.get(chartDiv.classList[1]);
            if (chartModel) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(event.href, null, chartModel.clone(), true);
            }
            return false;
        }
    }

    openAm5ChartDrilldown(event: any): void {
        const url = event.data;
        if (url) {
            let chartDiv = event.element;
            while (chartDiv && !chartDiv.classList.contains('agpr-mad-chartAnchor')) {
                chartDiv = chartDiv.parentElement;
            }
            if (!chartDiv) {
                return;
            }
            const chartModel = this.classToChartModelMap.get(chartDiv.classList[1]);
            if (chartModel) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(
                    url,
                    null,
                    chartModel.clone(),
                    true,
                    null,
                    null,
                    this.classToReportSeriesMap.get(chartDiv.classList[1])
                );
            }
        }
    }

    ngOnInit(): void {
        this.selectedMediaTypeBreakdownChartDropdownOption = this.mediaTypeBreakdownDropdownOptions[0];
        this.selectedMapChartDropdownOption = this.mapChartDropdownOptions[0];
        const noContentLabel = '<span style="font-size: 0.875rem;">' + this.localizationService.transform('Monitor.MAD.chart.noContentLabel') + '</span>';
        const resourceLoaded$: Observable<any> = merge(this.topicsService.currentResource$).pipe(
            filter(x => !!x),
            tap(x => {
                this.baseSentiments = x.searchCriteria.query.sentiments;
            }));
        this.subscriptions.push(
            resourceLoaded$.subscribe(),
            this.currentUserStateService.getUserCurrency().subscribe(x => this.currencyCode = x),
            this.madSummaryService.getSeriesMap().pipe(filter(x => !!x)).subscribe(seriesMap => {
                this.seriesMap = seriesMap;
                this.totalVolumeCardSeries = this.totalVolumeCardSeriesTypes.map(x => this.seriesMap.get(x));
                this.totalImpressionCardSeries = this.totalImpressionCardSeriesTypes.map(x => this.seriesMap.get(x));
                this.totalAVECardSeries = this.totalAVECardSeriesTypes.map(x => this.seriesMap.get(x));

                this.setupTrendChart();

                this.positiveCardSeries = this.positiveCardSeriesTypes.map(x => this.seriesMap.get(x));
                this.negativeCardSeries = this.negativeCardSeriesTypes.map(x => this.seriesMap.get(x));
                this.averageDomainAuthorityCardSeries = this.averageDomainAuthorityCardSeriesTypes.map(x => this.seriesMap.get(x));
                // COVERAGE BY MEDIA TYPE
                this.mediaTypeBreakdownSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.mediaTypeBreakdown);
                this.mediaTypeBreakdownSeries.mentions.filters.searchCriteria.query.includedMediaTypes = MediaTypeModel.getAllMediaTypes();
                this.mediaTypeBreakdownSeries.hasPreviousPeriod = true;
                this.mediaTypeBreakdownSeries.chartType = ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN;
                const [metric, sentiments] = this.getMediaTypeBreakdownChartQueriesFromOption(this.selectedMediaTypeBreakdownChartDropdownOption.value);
                this.mediaTypeBreakdownSeries.metric = metric;
                this.mediaTypeBreakdownSeries.mentions.filters.searchCriteria.query.sentiments = sentiments;
                const mediaTypeBreakdownStyles = new ChartStylesModel();
                Object.values(MediaTypeEnum).forEach(media => {
                    mediaTypeBreakdownStyles.colorMap[media] =
                        userAppPreferencesDefault.reportsPrefs.defaultChartColors?.mediaTypeColors?.[mediaTypeColorKeyMap.get(media)];
                });
                mediaTypeBreakdownStyles.chartDisplaySettings = new ChartDisplaySettingsModel();
                mediaTypeBreakdownStyles.chartDisplaySettings.previousPeriod = true;
                mediaTypeBreakdownStyles['chartHeaderSettings'] = new ChartDisplaySettingsModel(mediaTypeBreakdownStyles.chartDisplaySettings);
                mediaTypeBreakdownStyles['previousPeriodBarColorType'] = PreviousPeriodBarColorsEnum.USE_BAR;

                this.mediaTypeBreakdownChart = new CategoryValueXYChart(
                    [this.mediaTypeBreakdownSeries],
                    {
                        chartSpecificType: ChartSpecificTypesEnum.COLUMN,
                        hideLegend: true,
                        trendIndicators: true,
                        dataPointDistinctColor: true,
                        styles: mediaTypeBreakdownStyles,
                        barDisplayLimit: 25
                    } as CategoryValueXYChartOptions,
                    {
                        type: ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN
                    } as ChartMetadataModel,
                    this.reportsChartSeriesConverterService.getStackedColumnChartSeries(
                        ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN
                    )
                );

                this.mediaTypeBreakdownChart.overrideNoContent = true;

                // MAP CHART
                const mapStyles = new ChartStylesModel();
                mapStyles.colorMap = {
                    choroplethHighValue: chartColors.agprBlue,
                    choroplethLowValue: chartColors.agprLighterBlue
                };
                mapStyles.initialLatitude = 5;
                const mapChartSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.map);
                this.setMapChartMetricFromOptions(mapChartSeries, this.selectedMapChartDropdownOption);

                this.mapChart = new MapChartModel(
                    [mapChartSeries],
                    {
                        chartSpecificType: ChartSpecificTypesEnum.MAP,
                        colorScheme: MapChartColorSchemeEnum.CHOROPLETH,
                        header: false,
                        styles: mapStyles
                    } as AbstractChartOptionsModel,
                    {
                        type: ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
                    } as ChartMetadataModel
                );

                // TOP MENTIONS
                this.topMentionsSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.topMentions);
                this.topMentionsSeries.mentions.filters = new MentionSearchModel();
                let sortField;
                switch (this.selectedTopMentionsSortMetric) {
                    case MADChartOptionsEnum.IMPRESSIONS:
                        sortField =  SearchCriteriaSortFieldEnum.audience_reach;
                        break;
                    case MADChartOptionsEnum.AVE:
                        sortField =  SearchCriteriaSortFieldEnum.ave;
                        break;
                    case MADChartOptionsEnum.DOMAIN_AUTHORITY:
                        sortField =  SearchCriteriaSortFieldEnum.domainAuthority;
                        break;
                }
                this.topMentionsSeries.mentions.mentionRequestGenerator = () => {
                    return {
                        sortOrder: SortOrderEnum.descending,
                        sortField: sortField || SearchCriteriaSortFieldEnum.audience_reach,
                        pageSize: 5
                    };
                };

                // TOP OUTLETS AND AUTHORS
                this.topOutletsSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.topOutlets);
                this.topOutletsSeries.metric = this.selectedTopOutletsMetric;
                this.topOutletsSeries.chartType = ReportBlockTypeEnum.TOP_10_OUTLETS;
                this.topOutletsSeries.mentions.facetGenerator = () => {
                    return {
                        facet: this.madSummaryService.getTopEntitiesNeoFacet(this.topOutletsSeries.metric, EntityType.OUTLET),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                };
                const topOutletsDisplayElement = new ChartDisplayElementModel('topOutletsDisplayElement', [this.topOutletsSeries], new ChartStylesModel());
                topOutletsDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel({
                    title: true,
                    dateRange: false,
                    sourceLink: false,
                    entityProfile: true
                });
                topOutletsDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
                this.topOutletsBlock = new BlockModel(
                    null,
                    reportBlockTypesMap.get(ReportBlockTypeEnum.TOP_10_OUTLETS),
                    topOutletsDisplayElement
                );

                this.topAuthorsSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.topAuthors);
                this.topAuthorsSeries.metric = this.selectedTopAuthorsMetric;
                this.topAuthorsSeries.chartType = ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME;
                this.topAuthorsSeries.mentions.facetGenerator = () => {
                    return {
                        facet: this.madSummaryService.getTopEntitiesNeoFacet(this.topAuthorsSeries.metric, EntityType.CONTACT),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                };
                const topAuthorsDisplayElement = new ChartDisplayElementModel('topAuthorsDisplayElement', [this.topAuthorsSeries], new ChartStylesModel());
                topAuthorsDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel({
                    title: true,
                    dateRange: false,
                    sourceLink: false,
                    entityProfile: true
                });
                topAuthorsDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
                this.topAuthorsBlock = new BlockModel(
                    null,
                    reportBlockTypesMap.get(ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME),
                    topAuthorsDisplayElement
                );

                this.reportSeriesReady.emit(this.seriesMap);
                this.refreshClassToChartModelMap();

                this.preCacheSnapDomExports();
            }),
            // rebuild once the real date query is known/changes, see setupTrendChart()
            this.dashboardSearchService.getDateQuery().pipe(skip(1)).subscribe(() => {
                if (this.seriesMap) {
                    this.setupTrendChart();
                    this.refreshClassToChartModelMap();
                }
            })
        );

    }

    /*
        this is to help generate exports faster, we preCache them, so snapdom doesn't do a full load on export
    */
    preCacheSnapDomExports(): void {
        setTimeout(() => {
            ['madSummaryTrend', 'madSummaryTopMentions'].forEach(id =>
                this.chartExportService.preCacheSnapDom(document.getElementById(id))
            );
        }, 1000);
    }

    setupTrendChart(): void {
        this.summaryTrendLineSeries = [this.seriesMap.get(MADSummarySeriesTypesEnum.summaryTrend)];
        this.dashboardSearchService.getDateQuery().pipe(take(1)).subscribe(dateQuery => {
            this.summaryTrendLineSeries[0].dateRange = new ReportDateRangeModel().setFromDateQuery(dateQuery);
        });
        const summaryTrendLineStyles = new ChartStylesModel();
        summaryTrendLineStyles.colorMap = {
            'analysisColor1': chartColors.agprBlue,
            'analysisColor2': chartColors.agprPurple,
            'analysisColor3': chartColors.agprLightBlue
        };

        const chartSeries = this.reportsChartSeriesConverterService.getDateValueChartSeries(
            this.summaryTrendLineSeries, ReportBlockTypeEnum.COVERAGE_OVER_TIME, ChartSpecificTypesEnum.LINE, this.currencyCode
        );
        this.summaryTrendLineSeries[0].setChartType(ReportBlockTypeEnum.COVERAGE_OVER_TIME);

        const chartDisplayElement = new ChartDisplayElementModel('', this.summaryTrendLineSeries, summaryTrendLineStyles);
        chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
        const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(this.id, ReportBlockTypeEnum.COVERAGE_OVER_TIME);
        chartConfiguration.noContent.isMessageStyle = true;

        const chartSettings = this.am5ChartSettingsService.getChartSettings(
            ReportBlockTypeEnum.COVERAGE_OVER_TIME,
            chartDisplayElement
        );
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, false);
        });

        this.summaryTrendLineChart = new Am5DateValueXYChart(
            chartSeries,
            chartConfiguration,
            chartSettings
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    ngAfterViewInit(): void {
        this.classToChartModelMap = new Map<string, ChartModel | Am5Chart>([
            [this.summaryTrendLineChartClass, this.summaryTrendLineChart],
            [this.mapChartClass, this.mapChart],
            [this.mediaTypeBreakdownChartClass, this.mediaTypeBreakdownChart]
        ]);
        this.classToReportSeriesMap = new Map<string, ReportSeriesModel[]>([
            [this.summaryTrendLineChartClass, this.summaryTrendLineSeries]
        ]);
    }

    refreshClassToChartModelMap(): void {
        if (!this.classToChartModelMap) {
            return;
        }
        this.classToChartModelMap.set(this.summaryTrendLineChartClass, this.summaryTrendLineChart);
        this.classToChartModelMap.set(this.mapChartClass, this.mapChart);
        this.classToChartModelMap.set(this.mediaTypeBreakdownChartClass, this.mediaTypeBreakdownChart);
    }

    updateSummaryTrendLineChartNoData(noData: boolean): void {
        this.summaryTrendLineChartNoData = noData;
    }

    mapChartDropdownOptionChanged(option: DropdownOptionModel): void {
        const mapSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.map);
        const newSeries = mapSeries.clone();
        this.setMapChartMetricFromOptions(newSeries, option);
        const newMapChart = this.mapChart.clone() as MapChartModel;
        newMapChart.series = [newSeries];
        this.mapChart = newMapChart;
        this.refreshClassToChartModelMap();
        this.seriesMap.set(MADSummarySeriesTypesEnum.map, newSeries);
        this.reportSeriesChange.emit({ oldSeriesTypes: [MADSummarySeriesTypesEnum.map], newSeries: [newSeries] });
    }

    setMapChartMetricFromOptions(series: ReportSeriesModel, option: DropdownOptionModel): void {
        switch (option.value) {
            case MADChartOptionsEnum.VOLUME:
                series.metric = ChartMetricsEnum.volumeOfMentions;
                series.mentions.filters.searchCriteria.query.sentiments = this.baseSentiments;
                break;
            case MADChartOptionsEnum.IMPRESSIONS:
                series.metric = ChartMetricsEnum.IMPRESSIONS;
                series.mentions.filters.searchCriteria.query.sentiments = this.baseSentiments;
                break;
            case MADChartOptionsEnum.AVE:
                series.metric = ChartMetricsEnum.ave;
                series.mentions.filters.searchCriteria.query.sentiments = this.baseSentiments;
                break;
            case MADChartOptionsEnum.POSITIVE:
                series.metric = ChartMetricsEnum.volumeOfMentions;
                series.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
                break;
            case MADChartOptionsEnum.NEGATIVE:
                series.metric = ChartMetricsEnum.volumeOfMentions;
                series.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
                break;
        }
    }

    mediaTypeBreakdownDropdownOptionChange(option: MADChartOptionsEnum) {
        const seriesToUpdate = [];
        // Update both current and previous period series
        Array.from(this.dashboardSearchService.searchCriteriaMap.values()).forEach(searchCriteria => {
            searchCriteria.series.forEach(s => {
                if (s.chartType === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN) {
                    seriesToUpdate.push(s);
                }
            });
        });

        const [metric, sentiments] = this.getMediaTypeBreakdownChartQueriesFromOption(option);

        seriesToUpdate.forEach(x => {
            x.metric = metric;
            x.mentions.filters.searchCriteria.query.sentiments = sentiments;
        });
        this.mediaTypeBreakdownSeries.metric = metric;
        this.mediaTypeBreakdownSeries.mentions.filters.searchCriteria.query.sentiments = sentiments;

        const newChart = this.mediaTypeBreakdownChart.clone() as CategoryValueXYChart;
        newChart.series = [this.mediaTypeBreakdownSeries];
        this.mediaTypeBreakdownChart = newChart;
        this.refreshClassToChartModelMap();
        this.seriesMap.set(MADSummarySeriesTypesEnum.mediaTypeBreakdown, this.mediaTypeBreakdownSeries);
        this.reportSeriesChange.emit({ oldSeriesTypes: [MADSummarySeriesTypesEnum.map], newSeries: seriesToUpdate });
    }

    getMediaTypeBreakdownChartQueriesFromOption(option: MADChartOptionsEnum): [metric: ChartMetricsEnum, sentiment: SentimentModel[]] {
        let metric, sentiments;
        switch (option) {
            case MADChartOptionsEnum.VOLUME:
                metric = ChartMetricsEnum.VOLUME;
                sentiments = [];
                break;
            case MADChartOptionsEnum.IMPRESSIONS:
                metric = ChartMetricsEnum.IMPRESSIONS;
                sentiments = [];
                break;
            case MADChartOptionsEnum.AVE:
                metric = ChartMetricsEnum.AVE;
                sentiments = [];
                break;
            case MADChartOptionsEnum.POSITIVE:
                metric = ChartMetricsEnum.VOLUME;
                sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
                break;
            case MADChartOptionsEnum.NEGATIVE:
                metric = ChartMetricsEnum.VOLUME;
                sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
                break;
        }

        return [metric, sentiments];
    }

    updateMediaTypeBreakdownChartNoData(noData: boolean): void {
        this.mediaTypeBreakdownChartNoData = noData;
    }

    updateMapChartNoData(noData: boolean): void {
        this.mapChartNoData = noData;
    }

    topMentionsSeriesChange(value: {newSeries: ReportSeriesModel; metric?: MADChartOptionsEnum; sentiment?: MADChartOptionsEnum}) {
        this.topMentionsSeries = value.newSeries;
        this.selectedTopMentionsSortMetric = value.metric;
        this.reportSeriesChange.emit({ oldSeriesTypes: [MADSummarySeriesTypesEnum.topMentions], newSeries: [value.newSeries] });
    }

    openTopMentionsDrilldown() {
        const summaryTrendLineStyles = new ChartStylesModel();
        summaryTrendLineStyles.colorMap = {
            'analysisColor1': chartColors.agprBlue,
            'analysisColor2': chartColors.agprPurple,
            'analysisColor3': chartColors.agprLightBlue
        };
        const summaryTrendLineSeries = this.seriesMap.get(MADSummarySeriesTypesEnum.summaryTrend);
        summaryTrendLineSeries.metric = this.topMentionsSeries.metric;
        const chartModel = new DateValueXYChart(
            [summaryTrendLineSeries],
            {
                chartSpecificType: ChartSpecificTypesEnum.MULTI_LINE,
                unit: DateAndValueXYChartUnitEnum.NUMBER,
                styles: summaryTrendLineStyles
            } as DateAndValueXYChartOptionsModel,
            {
                type: ReportBlockTypeEnum.COVERAGE_OVER_TIME
            } as ChartMetadataModel,
            this.reportsChartSeriesConverterService.getDateValueChartSeries(
                [summaryTrendLineSeries], ReportBlockTypeEnum.COVERAGE_OVER_TIME, ChartSpecificTypesEnum.MULTI_LINE, null
            )
        );
        this.reportsChartDetailService.viewCoverageAnalysisDialog(
            summaryTrendLineSeries.getMentionSeriesUrl(),
            null,
            chartModel.clone(),
            true
        );
    }
    topEntitiesChartSeriesUpdate(value: {newSeries: ReportSeriesModel; entityType: EntityType}) {
        let seriesType;
        if (value.entityType === EntityType.CONTACT) {
            seriesType = MADSummarySeriesTypesEnum.topAuthors;
            this.topAuthorsSeries = value.newSeries;
            this.selectedTopAuthorsMetric = value.newSeries.metric;
            this.topAuthorsSeries.mentions.facetGenerator = () => {
                return {
                    facet: this.madSummaryService.getTopEntitiesNeoFacet(this.selectedTopAuthorsMetric, value.entityType),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            };
        } else {
            seriesType = MADSummarySeriesTypesEnum.topOutlets;
            this.topOutletsSeries = value.newSeries;
            this.selectedTopOutletsMetric = value.newSeries.metric;
            this.topOutletsSeries.mentions.facetGenerator = () => {
                return {
                    facet: this.madSummaryService.getTopEntitiesNeoFacet(this.selectedTopOutletsMetric, value.entityType),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            };
        }
        this.reportSeriesChange.emit({ oldSeriesTypes: [seriesType], newSeries: [value.newSeries] });
    }

    async summaryTrendExport(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        await this.chartExportService.exportImageSnapDomWithOverrides(
            event.eventKey.toLowerCase() as BlobType,
            elementRef,
            this.prepareExportData(chartType).title,
            [ { selector: '.agpr-madSummary-exportMenu', style: { display: 'none' } } ]
        );
    }

    async topMentionsExport(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        await this.chartExportService.exportImageSnapDomWithOverrides(
            event.eventKey.toLowerCase() as BlobType,
            elementRef,
            this.prepareExportData(chartType).title,
            topMentionsMADExportStyleOverrides
        );
    }

    public async exportChart(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        const exportModel = this.prepareExportData(chartType);
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            await this.chartExportService.exportImageSnapDomWithOverrides(
                exportType,
                elementRef,
                exportModel.title,
                [ { selector: '.agpr-madSummary-exportMenu', style: { display: 'none' } } ]
            );
        }
    }
    private prepareExportData(chartType: ReportBlockTypeEnum): SocialChartExportModel {
        switch (chartType) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                return {
                    title: this.localizationService.transform('Monitor.MAD.summary.summaryTrend'),
                };
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                return {
                    title: this.localizationService.transform('Monitor.MAD.summary.mediaTypeBreakdown.title'),
                    chartModel: this.mediaTypeBreakdownChart
                };
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                return {
                    title: this.localizationService.transform('Monitor.MAD.summary.mapChart.title'),
                };
            case ReportBlockTypeEnum.TOP_MENTIONS:
                return {
                    // eslint-disable-next-line max-len
                    title: `${this.localizationService.transform('Monitor.MAD.topMentions.title') }  ${this.localizationService.transform('Monitor.MAD.topMentions.title2')} ${this.localizationService.transform(this.mapChartDropdownOptions[this.selectedTopMentionsSortMetric].label)}`,
                };
            default:
                break;
        }
        return;
    }
}
